/**
 * The 22 cell shape classes used by the terrain system.
 *
 * A partial cell is the intersection of the unit cell with a half plane n·p <= c, where p is in
 * cell-local coordinates [0,1]², n is an integer outward normal (pointing from solid to air) and
 * c an integer. Supported normals: axis (only as full/empty), 45° (±1,±1), 2:1 (±1,±2: shallow,
 * run 2 rise 1) and 1:2 (±2,±1: steep). 2:1 and 1:2 lines cross a cell in two ways ("halves").
 */

export type ShapeKind = 'empty' | 'full' | 's45' | 's21' | 's12';

export interface ShapeClass {
  id: number;
  kind: ShapeKind;
  n: [number, number];
  c: number;
  name: string;
}

export const EMPTY = 0;
export const FULL = 1;

function build(): ShapeClass[] {
  const out: ShapeClass[] = [
    { id: 0, kind: 'empty', n: [0, 0], c: 0, name: 'empty' },
    { id: 1, kind: 'full', n: [0, 0], c: 0, name: 'full' },
  ];
  const normals: [number, number, ShapeKind][] = [];
  for (const [sx, sy] of [[-1, 1], [1, 1], [1, -1], [-1, -1]]) normals.push([sx, sy, 's45']);
  for (const [sx, sy] of [[-1, 2], [1, 2], [1, -2], [-1, -2]]) normals.push([sx, sy, 's21']);
  for (const [sx, sy] of [[-2, 1], [2, 1], [2, -1], [-2, -1]]) normals.push([sx, sy, 's12']);
  for (const [nx, ny, kind] of normals) {
    const vals = [0, nx, ny, nx + ny];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    for (let c = lo + 1; c < hi; c++) {
      out.push({ id: out.length, kind, n: [nx, ny], c, name: `${kind}(${nx},${ny})${c - lo}` });
    }
  }
  return out;
}

export const SHAPES: ShapeClass[] = build();
export const SHAPE_COUNT = SHAPES.length; // 22

const byKey = new Map<string, number>(SHAPES.map((s) => [`${s.n[0]},${s.n[1]},${s.c}`, s.id]));

/** Shape id of half plane n·p <= c clipped to the unit cell (empty/full when the line misses the cell). */
export function shapeFromLine(nx: number, ny: number, c: number): number {
  const lo = Math.min(0, nx, ny, nx + ny);
  const hi = Math.max(0, nx, ny, nx + ny);
  if (c <= lo) return EMPTY;
  if (c >= hi) return FULL;
  const id = byKey.get(`${nx},${ny},${c}`);
  return id ?? -1;
}

export function insideShape(id: number, x: number, y: number): boolean {
  if (id === EMPTY) return false;
  if (id === FULL) return true;
  const s = SHAPES[id];
  return s.n[0] * x + s.n[1] * y <= s.c;
}

/** Fractional coverage per sample (row-major, row 0 = bottom) on an S×S grid, supersampled. */
export function shapeCoverage(id: number, S: number): Float32Array {
  const out = new Float32Array(S * S);
  const SS = 4;
  for (let j = 0; j < S; j++)
    for (let i = 0; i < S; i++) {
      let n = 0;
      for (let b = 0; b < SS; b++)
        for (let a = 0; a < SS; a++) {
          if (insideShape(id, (i + (a + 0.5) / SS) / S, (j + (b + 0.5) / SS) / S)) n++;
        }
      out[j * S + i] = n / (SS * SS);
    }
  return out;
}

/** Shape area fraction. */
export function shapeArea(id: number): number {
  const cov = shapeCoverage(id, 8);
  let s = 0;
  for (const v of cov) s += v;
  return s / 64;
}

const covCache = new Map<number, Float32Array[]>();
function coverages(S: number): Float32Array[] {
  let c = covCache.get(S);
  if (!c) {
    c = SHAPES.map((s) => shapeCoverage(s.id, S));
    covCache.set(S, c);
  }
  return c;
}

/** Best matching shape class for an S×S occupancy mask (values 0..1). err = mean abs difference. */
export function classifyMask(mask: ArrayLike<number>, S: number, allowed?: (id: number) => boolean): { id: number; err: number } {
  const covs = coverages(S);
  let best = 0;
  let bestErr = Infinity;
  for (let k = 0; k < covs.length; k++) {
    if (allowed && !allowed(k)) continue;
    const cv = covs[k];
    let e = 0;
    for (let i = 0; i < cv.length; i++) e += Math.abs(cv[i] - mask[i]);
    if (e < bestErr - 1e-9) {
      bestErr = e;
      best = k;
    }
  }
  return { id: best, err: bestErr / (S * S) };
}

/** Tile flag matrix (see format.ts matrixFromFlags): x' = a x + b y, y' = c x + d y about the cell centre. */
export function flagMatrix(f: number): [number, number, number, number] {
  let a = 1, b = 0, c = 0, d = 1;
  if (f & 4) { a = 0; b = 1; c = 1; d = 0; }
  if (f & 1) { a = -a; b = -b; }
  if (f & 2) { c = -c; d = -d; }
  return [a, b, c, d];
}

/** Shape id after applying tile flags to a cell of shape `id`. */
export function transformShape(id: number, f: number): number {
  if (id <= FULL || f === 0) return id;
  const s = SHAPES[id];
  const [a, b, c, d] = flagMatrix(f);
  const nx = a * s.n[0] + b * s.n[1];
  const ny = c * s.n[0] + d * s.n[1];
  // n·(p-.5) <= c - n·.5  =>  n'·(p'-.5) <= same
  const k = s.c - (s.n[0] + s.n[1]) / 2;
  const c2 = k + (nx + ny) / 2;
  return shapeFromLine(nx, ny, Math.round(c2));
}

/** Inverse flags (the flag group elements are their own inverse except the two 90° rotations). */
export function invertFlags(f: number): number {
  const [a, b, c, d] = flagMatrix(f);
  for (let g = 0; g < 8; g++) {
    const [a2, b2, c2, d2] = flagMatrix(g);
    if (a2 === a && b2 === c && c2 === b && d2 === d) return g;
  }
  return f;
}

/** Compose flags: result applies `first` then `second`. */
export function composeFlags(first: number, second: number): number {
  const [a1, b1, c1, d1] = flagMatrix(first);
  const [a2, b2, c2, d2] = flagMatrix(second);
  const a = a2 * a1 + b2 * c1;
  const b = a2 * b1 + b2 * d1;
  const c = c2 * a1 + d2 * c1;
  const d = c2 * b1 + d2 * d1;
  for (let g = 0; g < 8; g++) {
    const m = flagMatrix(g);
    if (m[0] === a && m[1] === b && m[2] === c && m[3] === d) return g;
  }
  return 0;
}

/** Which way the solid side of a shape touches each cell side: fraction of side covered [L, R, B, T]. */
export function sideCoverage(id: number): [number, number, number, number] {
  if (id === EMPTY) return [0, 0, 0, 0];
  if (id === FULL) return [1, 1, 1, 1];
  const N = 16;
  const f = (pts: [number, number][]) => pts.filter(([x, y]) => insideShape(id, x, y)).length / N;
  const L: [number, number][] = [], R: [number, number][] = [], B: [number, number][] = [], T: [number, number][] = [];
  for (let i = 0; i < N; i++) {
    const t = (i + 0.5) / N;
    L.push([0, t]);
    R.push([1, t]);
    B.push([t, 0]);
    T.push([t, 1]);
  }
  return [f(L), f(R), f(B), f(T)];
}

export function directionOf(id: number): ShapeKind {
  return SHAPES[id]?.kind ?? 'empty';
}

/** Solid part of a cell shape as a polygon in cell coordinates [0,1]² (y up). */
export function shapePoly(id: number): [number, number][] {
  if (id === EMPTY) return [];
  const sq: [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];
  if (id === FULL) return sq;
  const s = SHAPES[id];
  const f = (p: [number, number]) => s.n[0] * p[0] + s.n[1] * p[1] - s.c;
  const out: [number, number][] = [];
  for (let i = 0; i < 4; i++) {
    const a = sq[i], b = sq[(i + 1) % 4];
    const fa = f(a), fb = f(b);
    if (fa <= 0) out.push(a);
    if ((fa < 0 && fb > 0) || (fa > 0 && fb < 0)) {
      const t = fa / (fa - fb);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}

/** The slope of a partial cell shape as a segment in cell coordinates. */
export function slopeSegment(own: number): [[number, number], [number, number]] | null {
  const s = SHAPES[own];
  if (!s || own <= FULL) return null;
  const f = (p: [number, number]) => s.n[0] * p[0] + s.n[1] * p[1] - s.c;
  const sq: [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];
  const pts: [number, number][] = [];
  for (let i = 0; i < 4; i++) {
    const a = sq[i], b = sq[(i + 1) % 4];
    const fa = f(a), fb = f(b);
    if (fa === 0) pts.push(a);
    else if ((fa < 0 && fb > 0) || (fa > 0 && fb < 0)) {
      const t = fa / (fa - fb);
      pts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return pts.length >= 2 ? [pts[0], pts[1]] : null;
}
